import { useState } from "react";
import { useStripe, useElements, PaymentElement } from "@stripe/react-stripe-js";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";

// `onError` is optional: only the Infinity Touch checkout passes it, so every existing
// eDisplay/funeral-store caller behaves exactly as before. It exists because the order
// row is created before the card is charged, and a decline that nobody reports leaves
// that row pending forever, looking like a received order (TR-25 item 3).
export default function StripePayment({ clientSecret, formData, totalAmount, onSuccess, onError }) {
    const stripe = useStripe();
    const elements = useElements();
    const [isProcessing, setIsProcessing] = useState(false);
    const { setCartOpen } = useCart();
    const isFuneralStore = window.location.pathname.includes('/reseller/funeral-store');

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!stripe || !elements) {
            return;
        }

        setIsProcessing(true);

        const { error, paymentIntent } = await stripe.confirmPayment({
            elements,
            confirmParams: {
                payment_method_data: {
                    billing_details: {
                        name: `${formData.shipping_address.firstname} ${formData.shipping_address.lastname}`,
                        email: formData.contact_email,
                        address: {
                            line1: formData.shipping_address.address,
                            city: formData.shipping_address.city,
                            state: formData.shipping_address.state,
                            postal_code: formData.shipping_address.zipcode,
                            country: formData.shipping_address.country || 'US',
                        },
                    },
                },
                return_url: isFuneralStore
                    ? `${window.location.origin}/reseller/my-purchases`
                    : `${window.location.origin}/checkout/success`,
            },
            redirect: 'if_required',
        });

        if (error) {
            toast.error(error.message);
            onError?.(error);
            setIsProcessing(false);
        } else if (paymentIntent) {
            // For any resolved payment state, navigate to success page
            // The success page will poll the backend to confirm final status
            onSuccess(paymentIntent);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 mb-6">
                <PaymentElement
                    options={{
                        fields: {
                            billingDetails: {
                                name: 'never',
                                address: 'never',
                                email: 'never'
                            },
                        },
                        defaultValues: {
                            billingDetails: {
                                name: `${formData.shipping_address.firstname} ${formData.shipping_address.lastname}`,
                                email: formData.contact_email,
                                address: {
                                    line1: formData.shipping_address.address,
                                    city: formData.shipping_address.city,
                                    state: formData.shipping_address.state,
                                    postal_code: formData.shipping_address.zipcode,
                                    country: formData.shipping_address.country || 'US',
                                },
                            },
                        },
                    }}
                />
            </div>

            <Button
                type="submit"
                disabled={!stripe || isProcessing}
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white h-14 rounded-2xl font-bold text-lg shadow-lg shadow-indigo-100 transition-all active:scale-[0.98]"
            >
                {isProcessing ? (
                    <div className="flex items-center gap-2">
                        <Loader2 className="animate-spin" size={20} />
                        <span>Processing...</span>
                    </div>
                ) : (
                    `Pay $${totalAmount.toFixed(2)}`
                )}
            </Button>

            <p className="text-[10px] text-center text-slate-400 font-medium">
                Your payment is secured by industry-standard SSL encryption.
            </p>
        </form>
    );
}
